<template>
    <div>
        <div
            class="container"
            :class="cClass">
            <div
                class="felt"
                :class="stepClasses">
                <div class="in-step text-center">
                    <template v-if="editTime">
                        <div class="inline-block w-[190px]">
                            <hts-form-date
                                v-model="task.ata"
                                label="Bắt đầu tác vụ"
                                type="datetime"
                                class="w-full"
                                :clearable="clearable"
                                @change="handleChangeTime($event, 'ata')" />
                            <p class="text-red-500">
                &nbsp;
                                <span
                                    v-if="hasInError"
                                    class="travel-error">
                                    Thời gian nhập lỗi
                                </span>
                            </p>
                        </div>
                        <div class="inline-block w-[190px]">
                            <hts-form-date
                                v-model="task.atd"
                                label="Kết thúc tác vụ"
                                type="datetime"
                                class="w-full"
                                :clearable="clearable"
                                @change="handleChangeTime($event, 'atd')" />
                            <p class="text-red-500">
                &nbsp;
                                <span
                                    v-if="hasOutError"
                                    class="travel-error">
                                    Thời gian nhập lỗi
                                </span>
                            </p>
                        </div>
                    </template>
                    <template v-else>
                        <div
                            class="text-sm"
                            :class="{
                                'text-main-text': task.processAlertStatus > 10,
                                'text-error-500': task.processAlertStatus > 30,
                                'opacity-50': task.disable
                            }">
                            <template v-if="isSameDate">
                                <p class="text-right font-bold">
                                    <span
                                        class="time-start"
                                        :class="stepClasses">
                                        {{ getStartTime() }}
                                    </span><span>
                                        -
                                    </span><span
                                        class="time-end"
                                        :class="stepClasses">
                                        {{ getEndTime() }}
                                    </span>
                                </p>
                                <p
                                    class="text-right time-end"
                                    :class="stepClasses">
                                    {{ getDateRange() }}
                                </p>
                            </template>
                            <template v-else>
                                <div class="flex justify-end items-center gap-2">
                                    <div>
                                        <p
                                            class="text-right font-bold time-start"
                                            :class="stepClasses">
                                            {{ getStartTime() }}
                                        </p>
                                        <p
                                            class="text-right time-start"
                                            :class="stepClasses">
                                            {{ getStartDate() }}
                                        </p>
                                    </div>
                                    <span class="font-bold">
                                        -
                                    </span>
                                    <div>
                                        <p
                                            class="font-bold time-end"
                                            :class="stepClasses">
                                            {{ getEndTime() }}
                                        </p>
                                        <p
                                            class="time-end"
                                            :class="stepClasses">
                                            {{ getEndDate() }}
                                        </p>
                                    </div>
                                </div>
                            </template>
                        </div>
                    </template>
                </div>
                <div class="in-line" />
            </div>
            <div
                class="step-item"
                :class="stepClasses">
                <div class="step-circle step-circle-small" />
                <div class="step-line step-line-start" />
                <div class="step-line step-line-end" />
                <img
                    class="step-vehicle"
                    :src="vehicleImg" >
            </div>
            <div
                class="right"
                :class="stepClasses">
                <div class="in-step">
                    <div class="flex items-center gap-2 w-full">
                        <span class="text-gray-600 font-bold max-w-[70%] truncate text-sm">
                            <span v-if="task.typeTask === 'waiting'">
                                Chờ
                            </span>
                            <span v-else>
                                {{ task.taskName }}
                            </span>
                        </span>
                        <div class="relative">
                            <span class="absolute top-0 left-0 -translate-y-3 flex gap-1">
                                <hts-icons
                                    v-if="task.processAlertStatus > 30"
                                    svg="warning-sign"
                                    class="inline-block"
                                    color="red" />
                                <div
                                    v-if="!hasActual && isValidWaitingTime(task)"
                                    class="inline-flex items-center">
                                    <hts-icons
                                        svg="hourglass"
                                        fill-color="orange"
                                        class="inline-block" />
                                    <span class="inline-block whitespace-nowrap">
                                        {{ $formatters.duration(task.waitingTime, 's') }}
                                    </span>
                                </div>
                                <hts-icons
                                    v-if="task.driverAlert"
                                    svg="driver"
                                    color="purple"
                                    class="inline-block" />
                                <hts-icons
                                    v-if="task.hasAttachFile"
                                    svg="attach-file"
                                    fill-color="#333333"
                                    class="inline-block" />
                            </span>
                        </div>
                    </div>
                    <template v-if="Array.isArray(task.timingStartArr)">
                        <p
                            v-for="time in task.timingStartArr"
                            :key="time"
                            class="task-item-condition"
                            :class="{
                                earliest: time.timeType === 'EARLIEST',
                                latest: time.timeType === 'LATEST'
                            }">
                            {{ time.label }}
                        </p>
                    </template>
                    <template v-if="Array.isArray(task.timingEndArr)">
                        <p
                            v-for="time in task.timingEndArr"
                            :key="time"
                            class="task-item-condition"
                            :class="{
                                earliest: time.timeType === 'EARLIEST',
                                latest: time.timeType === 'LATEST'
                            }">
                            {{ time.label }}
                        </p>
                    </template>
                </div>
                <div class="in-line" />
                <div class="right" />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { isValidWaitingTime } from './-utils/timing'

const vehicleImg = ref('/assets/vehicle.png')

const { $dayjs, $formatters } = useNuxtApp()

const props = defineProps({
  task: {
    type: Object,
    required: true
  },
  taskIndex: Number,
  nextTask: Object,
  isLastStage: Boolean,
  isLast: Boolean,
  editTime: Boolean,
  hasInError: Boolean,
  hasOutError: Boolean,
  dateFormat: String,
  clearable: Boolean,
  showVehicle: Boolean
})
const { task, nextTask, editTime, showVehicle, dateFormat } = toRefs(props)
const emits = defineEmits(['clickTask', 'update-time'])

const cClass = computed(() => {
  return {
    done: taskProcess.value === 'done',
    'show-vehicle': showVehicle.value
  }
})
const ignoreTime = computed(() => !!task.value.ignoreTime)
const hasActual = computed(() => !!task.value.ata || !!task.value.atd)
const taskProcess = computed<any>(() => {
  if (task.value.atd) {
    return 'done'
  }
  if (task.value.ata) {
    return 'in'
  }
  return null
})

const stepClasses = computed(() => {
  return {
    editing: editTime.value,
    'step-in': ignoreTime.value || taskProcess.value === 'in',
    'step-done': ignoreTime.value || ['out', 'done'].includes(taskProcess.value),
    'show-vehicle': showVehicle.value
  }
})
const startTime = computed(() => {
  const { eta, ataEstimate, ata } = task.value
  return ata || ataEstimate || eta
})
const endTime = computed(() => {
  const { etd, atdEstimate, atd } = task.value
  return atd || atdEstimate || etd
})
const getStartTime = () => {
  const start = $dayjs(startTime.value).format('HH:mm')
  return start
}
const getEndTime = () => {
  const end = $dayjs(endTime.value).format('HH:mm')
  return end
}
const getStartDate = () => {
  const { eta, ata } = task.value
  const start = $dayjs(startTime.value)
  return $formatters.date(start, dateFormat?.value)
}
const getEndDate = () => {
  const end = $dayjs(endTime.value)
  return $formatters.date(end, dateFormat?.value)
}
const getTimeRange = () => {
  const start = $dayjs(startTime.value).format('HH:mm')
  const end = $dayjs(endTime.value).format('HH:mm')
  return start + ' - ' + end
}
const isSameDate = computed(() => {
  const start = $dayjs(startTime.value)
  const end = $dayjs(endTime.value)
  return start.isSame(end, 'date')
})
const getDateRange = () => {
  const start = $dayjs(startTime.value)
  const end = $dayjs(endTime.value)
  if (start.isSame(end, 'date')) {
    return $formatters.date(start, dateFormat?.value)
  }
  return $formatters.date(start, dateFormat?.value) + ' - ' + $formatters.date(end, dateFormat?.value)
}

const clickTask = (step: any) => {
  emits('clickTask', step)
}
const handleChangeTime = (val: any, type: string) => {
  emits('update-time', { value: val, type, item: task.value })
}
</script>
